import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useAppContext } from "../../data/context.tsx";
import {
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  X,
} from "lucide-react";
import VerticalNavigation from "../../components/verticalNavigation/verticalNavigation.tsx";
import Slider2 from "../../components/slider2/slider2.tsx";
import Slider3 from "../../components/slider3/slider3.tsx";
import SliderConnector from "../../components/sliderConnector/sliderConnector.tsx";
import Tooltip from "../../components/tooltip/tooltip.tsx";
import TruckCapacitySelector from "../../components/truckCapacitySelector/TruckCapacitySelector.tsx";
import SettingsButton from "../../components/settingsButton/settingsButton.tsx";
import RadioGroup from "../../components/radioGroup/radioGroup.tsx";
import Feasibility from "../../components/feasibility/feasibility.tsx";
import CardList from "../../components/cardList/cardList.tsx";
import NumberCard from "../../components/numberCard/numberCard.tsx";
import DotCard from "../../components/dotCard/dotCard.tsx";
import Download from "../../components/download/download.tsx";
import SaveAnalysisModal from "../../components/saveAnalysisModal/saveAnalysisModal.tsx";
import { useWizardTags } from "../../data/useWizardTags.ts";
import TabPanel from "../../components/tabPanel/tabPanel.tsx";
import ComparisonLineChart from "../../components/comparisonLineChart/comparisonLineChart.tsx";
import ComparisonRadarChart from "../../components/comparisonRadarChart/comparisonRadarChart.tsx";
import ComparisonTable from "../../components/comparisonTable/comparisonTable.tsx";
import ComparisonBarChart from "../../components/comparisonBarChart/comparisonBarChart.tsx";
import Login from "../../components/login/login.tsx";
import {

  scaleOptions,
  operationalLifeOptions,
  truckCapacityOptions,
  domains,
} from "./dummyData.ts";
import "./mcdaResults.css";
import { getMCDAResults, postMCDAResults, saveAnalysis } from "../../services/analysisService";
import { maxOfStructured } from '../../utilities/array.js';
import { AnalysisInput } from "../../types/analysisPayload";
import HealthCard from "../../components/HealthCard/HealthCard.tsx";
import InstitutionCard from "../../components/InstutionCard/InstitutionCard.tsx";
import LineDotCard from "../../components/LineDotCard/LineDotCard.tsx";
import PeopleCard from "../../components/PeopleCard/PeopleCard.tsx";



// Function to convert decimal degrees to degrees, minutes, seconds format
const formatCoordinate = (decimal: number, isLatitude: boolean): string => {
  const direction = isLatitude
    ? (decimal >= 0 ? 'N' : 'S')
    : (decimal >= 0 ? 'E' : 'W');

  const abs = Math.abs(decimal);
  const degrees = Math.floor(abs);
  const minutesFloat = (abs - degrees) * 60;
  const minutes = Math.floor(minutesFloat);
  const seconds = (minutesFloat - minutes) * 60;

  return `${degrees}° ${minutes}' ${seconds.toFixed(1)} ${direction}`;
};


// Function to convert Web Mercator to WGS84 (approximation)
const webMercatorToWGS84 = (coordinate: [number, number]): [number, number] => {
  const [x, y] = coordinate;

  // Check if coordinates are already in WGS84 range (rough check)
  if (Math.abs(x) <= 180 && Math.abs(y) <= 90) {
    return [x, y]; // Already in WGS84
  }

  // Convert from Web Mercator to WGS84
  const lng = (x / 20037508.34) * 180;
  const lat = (y / 20037508.34) * 180;
  const latRad = Math.atan(Math.exp(lat * Math.PI / 180)) * 2 - Math.PI / 2;
  const latDeg = latRad * 180 / Math.PI;

  return [lng, latDeg];
};

// Function to format location coordinates
const formatLocation = (coordinate: [number, number]): string => {
  // Convert from Web Mercator to WGS84 if needed
  const [lng, lat] = webMercatorToWGS84(coordinate);
  const latFormatted = formatCoordinate(lat, true);
  const lngFormatted = formatCoordinate(lng, false);
  return `${latFormatted} / ${lngFormatted}`;
};

interface AnalysisResult {
  businessModel: string;
  indexes: {
    environment: {
      waterUse: number;
      landUse: number;
      energyUse: number;
      noisePollutionRisk: number;
      odourPollutionRisk: number;
      wasteDiversionRate: number;
      biodiversityImpactScore: number;
      netGHG: number;
    };
    technology: {
      operationalReliabilityScore: number;
      resourceRecoveryPotential: number;
      maturityAndReadinessLevel: number;
      compatibilityIndex: number;
      complexityIndex: number;
      nitrogenRecoveryPotential: number;
      phosphorousRecoveryPotential: number;
      potassiumRecoveryPotential: number;
    };
    finance: {
      internalRateOfReturn: number;
      returnOnInvestment: number;
      benefitToCostRatio: number;
      paybackPeriod: number;
    };
    people: {
      jobCreationPotential: number;
      genderAndYouthInclusionIndex: number;
      socialAcceptanceScore: number;
    };
    health: {
      occupationalHealthAndSafetyScore: number;
      healthMitigationCapacityIndex: number;
    };
    institutions: {
      policyAndInstitutionalSupportScore: number;
      regulatoryComplianceEase: number;
      financialAndIncentiveMechanismScore: number;
      landAccessAndTenureSecurityScore: number;
    };
    markets: {
      marketDemandPotentialScore: number;
      priceCompetitivenessScore: number;
      marketInfrastructureAndDistributionScore: number;
      marketAccessibilityAndInclusivenessScore: number;
      standardCertificationAndBuyerConfidenceScore: number;
    };
  };
  feasibilityIndex: number;
}

interface MCDAResponse {
  originalInput: AnalysisInput;
  analysisResults: AnalysisResult[];
}

interface AuthState {
  isLoggedIn: boolean;
  user: any;
}

interface RootState {
  auth: AuthState;
}

const MCDAResults: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = useAppContext();

  const { isLoggedIn } = useSelector((state: RootState) => state.auth);
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [isLoginOpen, setIsLoginOpen] = useState<boolean>(false);

  const [environmentRadarDataState, setEnvironmentRadarData] = useState<any[]>([]);
  const [environmentRadarDatasetsState, setEnvironmentRadarDatasets] = useState<any[]>([]);
  const [environmentTableDataState, setEnvironmentTableData] = useState<any[]>([]);
  const [environmentColorsState, setEnvironmentColors] = useState<string[]>([]);
  const [environmentLegendLabelsState, setEnvironmentLegendLabels] = useState<string[]>([]);

  const [financeBarDataState, setFinanceRadarData] = useState<any[]>([]);
  const [financeBarDatasetsState, setFinanceRadarDatasets] = useState<any[]>([]);
  const [financeTableDataState, setFinanceTableData] = useState<any[]>([]);
  const [financeColorsState, setFinanceColors] = useState<string[]>([]);
  const [financeLegendLabelsState, setFinanceLegendLabels] = useState<string[]>([]);

  const [cumulativeCashFlowState, setCumulativeCashFlow] = useState<string[]>([]);
  const [cumulativeCashFlowDatasetsState, setCumulativeCashFlowDatasets] = useState<string[]>([]);


  
  const [technologyBarDataState, setTechnologyBarData] = useState<any[]>([]);
  const [technologyBarDatasetsState, setTechnologyBarDatasets] = useState<any[]>([]);
  const [technologyTableDataState, setTechnologyTableData] = useState<any[]>([]);
  const [technologyColorsState, setTechnologyColors] = useState<string[]>([]);
  const [technologyLegendLabelsState, setTechnologyLegendLabels] = useState<string[]>([]);

  const [institutionsRadarDataState, setInstitutionsRadarData] = useState<any[]>([]);
  const [institutionsRadarDatasetsState, setInstitutionsRadarDatasets] = useState<any[]>([]);
  const [institutionsTableDataState, setInstitutionsTableData] = useState<any[]>([]);
  const [institutionsColorsState, setInstitutionsColors] = useState<string[]>([]);
  const [institutionsLegendLabelsState, setInstitutionsLegendLabels] = useState<string[]>([]);

  const [peopleRadarDataState, setPeopleRadarData] = useState<any[]>([]);
  const [peopleRadarDatasetsState, setPeopleRadarDatasets] = useState<any[]>([]);
  const [peopleTableDataState, setPeopleTableData] = useState<any[]>([]);
  const [peopleColorsState, setPeopleColors] = useState<string[]>([]);
  const [peopleLegendLabelsState, setPeopleLegendLabels] = useState<string[]>([]);

  const [marketsRadarDataState, setMarketsRadarData] = useState<any[]>([]);
  const [marketsRadarDatasetsState, setMarketsRadarDatasets] = useState<any[]>([]);
  const [marketsTableDataState, setMarketsTableData] = useState<any[]>([]);
  const [marketsColorsState, setMarketsColors] = useState<string[]>([]);
  const [marketsLegendLabelsState, setMarketsLegendLabels] = useState<string[]>([]);

  const [healthBarDataState, setHealthRadarData] = useState<any[]>([]);
  const [healthBarDatasetsState, setHealthRadarDatasets] = useState<any[]>([]);
  const [healthTableDataState, setHealthTableData] = useState<any[]>([]);
  const [healthColorsState, setHealthColors] = useState<string[]>([]);
  const [healthLegendLabelsState, setHealthLegendLabels] = useState<string[]>([]);

  const [cardDataMap, setCardDataMap] = useState<Record<string, any>>({});
  const [analysisResponse, setAnalysisResponse] = useState<MCDAResponse | null>(null);


  const processResponse = (data: MCDAResponse) => {
    const originalResults = data.analysisResults;
    if (!originalResults || originalResults.length === 0) return;

    // Sort results by feasibilityIndex descending to ensure ranking order
    const results = [...originalResults].sort((a, b) => b.feasibilityIndex - a.feasibilityIndex);

    // 1. Generate Colors and Keys
    const colors = ["var(--color-very-dark-blue)", "var(--color-dark-blue)", "var(--color-light-blue)"];

    // Sanitize business model names to create valid keys
    const modelKeys = results.map(r => r.businessModel.replace(/[^a-zA-Z0-9]/g, ''));
    const legendLabels = results.map(r => r.businessModel);

    // 2. Prepare Environment Data
    const envSubjects = [
      { key: 'waterUse', label: 'Water Use [0-5]', unit: '' },
      { key: 'landUse', label: 'Land Use [0-5]', unit: '' },
      { key: 'energyUse', label: 'Energy Use [0-5]', unit: '' },
      { key: 'wasteDiversionRate', label: 'Waste Diversion [0-5]', unit: '' },
      { key: 'netGHG', label: 'Net GHG [tCO2e/t]', unit: '' }
    ];

    const newEnvRadarData = envSubjects
      .filter(subj => subj.label != "Net GHG")
      .map(subj => {
        const entry: any = { subject: subj.label, min: 0, max: 100 }; // Normalize if needed, currently raw
        results.forEach((r, idx) => {
          // Simple normalization for visualization if needed, or use raw values
          // For now using raw values, but might need scaling based on min/max of the set
          entry[modelKeys[idx]] = r.indexes.environment[subj.key as keyof typeof r.indexes.environment];
        });
        return entry;
      });

    const newEnvTableData = envSubjects.map(subj => {
      return {
        label: subj.label,
        values: results.map(r => r.indexes.environment[subj.key as keyof typeof r.indexes.environment]),
        unit: subj.unit
      };
    });

    const newEnvDatasets = modelKeys.map((key, idx) => ({
      dataKey: key,
      fillColor: colors[idx % colors.length],
      strokeColor: colors[idx % colors.length],
      fillOpacity: 0.3,
      strokeWidth: 2,
    }));

    setEnvironmentRadarData(newEnvRadarData);
    setEnvironmentRadarDatasets(newEnvDatasets);
    setEnvironmentTableData(newEnvTableData);
    setEnvironmentColors(colors.slice(0, results.length));
    setEnvironmentLegendLabels(legendLabels);

    // 3. Prepare Finance Data
    const finSubjects = [
      { key: 'internalRateOfReturn', label: 'IRR [%]', unit: '' },
      { key: 'returnOnInvestment', label: 'ROI [%]', unit: '' },
      { key: 'benefitToCostRatio', label: 'BCR [-]', unit: '' },
      { key: 'paybackPeriod', label: 'Payback [years]', unit: '' }
    ];

    const newFinanceBarData = finSubjects
      .filter(subj => subj.label != "BCR" && subj.label != "Payback")
      .map(subj => {
        const entry: any = { subject: subj.label, min: 0, max: 100 };
        results.forEach((r, idx) => {
          entry[modelKeys[idx]] = r.indexes.finance[subj.key as keyof typeof r.indexes.finance];
        });
        return entry;
      });

    const newFinTableData = finSubjects.map(subj => {
      return {
        label: subj.label,
        values: results.map(r => r.indexes.finance[subj.key as keyof typeof r.indexes.finance]),
        unit: subj.unit
      };
    });

    const newFinDatasets = modelKeys.map((key, idx) => ({
      dataKey: key,
      fillColor: colors[idx % colors.length],
      strokeColor: colors[idx % colors.length],
      fillOpacity: 0.3,
      strokeWidth: 2,
    }));

    setFinanceRadarData(newFinanceBarData);
    setFinanceRadarDatasets(newFinDatasets);
    setFinanceTableData(newFinTableData);
    setFinanceColors(colors.slice(0, results.length));
    setFinanceLegendLabels(legendLabels);




    // CumulativeCashFlow data
    const years = Object.keys(results[0].indexes.internalIndicators.financialAnalysis.years);

    const newCashFlowData = years.map((year) => {
      const entry: any = { subject: year };
    
      results.forEach((r, idx) => {
        const modelKey = modelKeys[idx];
        const yearData = r.indexes.internalIndicators.financialAnalysis.years[year];
        entry[modelKey] = yearData.cumulativeCashFlow;
      });
    
      return entry;
    });
    
    const newCashFlowDatasets = modelKeys.map((key, idx) => ({
      dataKey: key,
      strokeColor: colors[idx % colors.length],
      strokeWidth: 3,
    }));

    setCumulativeCashFlow(newCashFlowData);
    setCumulativeCashFlowDatasets(newCashFlowDatasets);



    
    // 4. Prepare Technology Data (Bar Chart)
    const techSubjects = [
      { key: 'operationalReliabilityScore', label: 'Operational Reliability [0-5]' },
      { key: 'resourceRecoveryPotential', label: `Resource Recovery [${currentPayload?.valueAddedProduct === "Energy" ? "kWh/t" : "kg/t"}]` }
    ];

    const newTechBarData = techSubjects.map(subj => {
      const entry: any = { category: subj.label };
      // Tech bar chart structure usually expects separate min/max or positive/negative logic
      // Adapting to simple positive bars for now
      results.forEach((r, idx) => {
        entry[modelKeys[idx]] = r.indexes.technology[subj.key as keyof typeof r.indexes.technology];
      });
      return entry;
    });

    const newTechTableData = techSubjects.map(subj => {
      return {
        label: subj.label,
        values: results.map(r => r.indexes.technology[subj.key as keyof typeof r.indexes.technology]),
        unit: ''
      };
    });

    const newTechDatasets = modelKeys.map((key, idx) => ({
      dataKey: key,
      fillColor: colors[idx % colors.length],
      strokeColor: colors[idx % colors.length],
      fillOpacity: 0.3,
      strokeWidth: 2,
    }));

    setTechnologyBarData(newTechBarData);
    setTechnologyBarDatasets(newTechDatasets);
    setTechnologyTableData(newTechTableData);
    setTechnologyColors(colors.slice(0, results.length));
    setTechnologyLegendLabels(legendLabels);

    // 5. Prepare Institutions Data
    const instSubjects = [
      { key: 'policyAndInstitutionalSupportScore', label: 'Policy Support [0-5]' },
      { key: 'regulatoryComplianceEase', label: 'Regulatory Compliance [0-5]' },
      { key: 'financialAndIncentiveMechanismScore', label: 'Financial Incentives [0-5]' },
      { key: 'landAccessAndTenureSecurityScore', label: 'Land Access [0-5]' }
    ];

    const newInstRadarData = instSubjects.map(subj => {
      const entry: any = { subject: subj.label, min: 0, max: 100 };
      results.forEach((r, idx) => {
        entry[modelKeys[idx]] = r.indexes.institutions[subj.key as keyof typeof r.indexes.institutions];
      });
      return entry;
    });

    const newInstTableData = instSubjects.map(subj => {
      return {
        label: subj.label,
        values: results.map(r => r.indexes.institutions[subj.key as keyof typeof r.indexes.institutions]),
        unit: ''
      };
    });

    const newInstDatasets = modelKeys.map((key, idx) => ({
      dataKey: key,
      fillColor: colors[idx % colors.length],
      strokeColor: colors[idx % colors.length],
      fillOpacity: 0.3,
      strokeWidth: 2,
    }));

    setInstitutionsRadarData(newInstRadarData);
    setInstitutionsRadarDatasets(newInstDatasets);
    setInstitutionsTableData(newInstTableData);
    setInstitutionsColors(colors.slice(0, results.length));
    setInstitutionsLegendLabels(legendLabels);

    // 6. Prepare People Data
    const peopleSubjects = [
      { key: 'jobCreationPotential', label: 'Job Creation [0-5]' },
      { key: 'genderAndYouthInclusionIndex', label: 'Inclusion [0-5]' },
      { key: 'socialAcceptanceScore', label: 'Social Acceptance [0-5]' }
    ];

    const newPeopleRadarData = peopleSubjects.map(subj => {
      const entry: any = { subject: subj.label, min: 0, max: 100 };
      results.forEach((r, idx) => {
        entry[modelKeys[idx]] = r.indexes.people[subj.key as keyof typeof r.indexes.people];
      });
      return entry;
    });

    const newPeopleTableData = peopleSubjects.map(subj => {
      return {
        label: subj.label,
        values: results.map(r => r.indexes.people[subj.key as keyof typeof r.indexes.people]),
        unit: ''
      };
    });

    const newPeopleDatasets = modelKeys.map((key, idx) => ({
      dataKey: key,
      fillColor: colors[idx % colors.length],
      strokeColor: colors[idx % colors.length],
      fillOpacity: 0.3,
      strokeWidth: 2,
    }));

    setPeopleRadarData(newPeopleRadarData);
    setPeopleRadarDatasets(newPeopleDatasets);
    setPeopleTableData(newPeopleTableData);
    setPeopleColors(colors.slice(0, results.length));
    setPeopleLegendLabels(legendLabels);

    // 7. Prepare Markets Data
    const marketSubjects = [
      { key: 'marketDemandPotentialScore', label: 'Market Demand [0-5]' },
      { key: 'priceCompetitivenessScore', label: 'Price Competitiveness [0-5]' },
      { key: 'marketInfrastructureAndDistributionScore', label: 'Infrastructure [0-5]' },
      { key: 'marketAccessibilityAndInclusivenessScore', label: 'Accessibility [0-5]' },
      { key: 'standardCertificationAndBuyerConfidenceScore', label: 'Buyer Confidence [0-5]' }
    ];

    const newMarketRadarData = marketSubjects.map(subj => {
      const entry: any = { subject: subj.label, min: 0, max: 100 };
      results.forEach((r, idx) => {
        entry[modelKeys[idx]] = r.indexes.markets[subj.key as keyof typeof r.indexes.markets];
      });
      return entry;
    });

    const newMarketTableData = marketSubjects.map(subj => {
      return {
        label: subj.label,
        values: results.map(r => r.indexes.markets[subj.key as keyof typeof r.indexes.markets]),
        unit: ''
      };
    });

    const newMarketDatasets = modelKeys.map((key, idx) => ({
      dataKey: key,
      fillColor: colors[idx % colors.length],
      strokeColor: colors[idx % colors.length],
      fillOpacity: 0.3,
      strokeWidth: 2,
    }));

    setMarketsRadarData(newMarketRadarData);
    setMarketsRadarDatasets(newMarketDatasets);
    setMarketsTableData(newMarketTableData);
    setMarketsColors(colors.slice(0, results.length));
    setMarketsLegendLabels(legendLabels);

    // 8. Prepare Health Data
    const healthSubjects = [
      { key: 'occupationalHealthAndSafetyScore', label: 'Occupational Safety [0-5]' },
      { key: 'healthMitigationCapacityIndex', label: 'Health Mitigation [0-5]' }
    ];

    const newHealthBarData = healthSubjects.map(subj => {
      const entry: any = { subject: subj.label, min: 0, max: 5 };
      results.forEach((r, idx) => {
        entry[modelKeys[idx]] = r.indexes.health[subj.key as keyof typeof r.indexes.health];
      });
      return entry;
    });

    const newHealthTableData = healthSubjects.map(subj => {
      return {
        label: subj.label,
        values: results.map(r => r.indexes.health[subj.key as keyof typeof r.indexes.health]),
        unit: ''
      };
    });

    const newHealthDatasets = modelKeys.map((key, idx) => ({
      dataKey: key,
      fillColor: colors[idx % colors.length],
      strokeColor: colors[idx % colors.length],
      fillOpacity: 0.3,
      strokeWidth: 2,
    }));

    setHealthRadarData(newHealthBarData);
    setHealthRadarDatasets(newHealthDatasets);
    setHealthTableData(newHealthTableData);
    setHealthColors(colors.slice(0, results.length));
    setHealthLegendLabels(legendLabels);

    // 5. Prepare Card Data
    const newCardDataMap: Record<string, any> = {};
    results.forEach((r, idx) => {
      const key = modelKeys[idx];
      newCardDataMap[key] = {
        position: `${idx + 1}${idx === 0 ? 'st' : idx === 1 ? 'nd' : idx === 2 ? 'rd' : 'th'}`,
        title: r.businessModel,
        value: r.feasibilityIndex,
        color: colors[idx % colors.length],
        numberCardData: [// environment
          { label: "Water Use [0-5]", value: r.indexes.environment.waterUse },
          { label: "Land Use [0-5]", value: r.indexes.environment.landUse },
          { label: "Energy Use [0-5]", value: r.indexes.environment.energyUse },
        ],
        cardListData: [ // finance
          { label: "IRR [%]", value: typeof r.indexes.finance.internalRateOfReturn === 'number' ? r.indexes.finance.internalRateOfReturn.toFixed(2) : '0.00', unit: "" },
          { label: "ROI [%]", value: typeof r.indexes.finance.returnOnInvestment === 'number' ? r.indexes.finance.returnOnInvestment.toFixed(2) : '0.00', unit: "" },
          { label: "BCR [-]", value: typeof r.indexes.finance.benefitToCostRatio === 'number' ? r.indexes.finance.benefitToCostRatio.toFixed(2) : '0.00' },
        ],
        dotCardData: { // technology
          category: r.businessModel,
          title: "Technology",
          data:
            [
              { label: "Resource Recovery", value: r.indexes.technology.resourceRecoveryPotential },
              { label: "Reliability", value: Math.round(r.indexes.technology.operationalReliabilityScore * 5) },
            ],
          subtitle1: "Resource Recovery",
          value: r.indexes.technology.resourceRecoveryPotential,
          unit: (currentPayload.valueAddedProduct == "Energy") ? "kWh/t" : "kg/t",
          subtitle2: "Reliability",
          score: Math.round(r.indexes.technology.operationalReliabilityScore), // Scale 0-1 to 1-5 stars if needed
        },
        healthCardData: { // health
          category: r.businessModel,
          title: "Health",
          subtitle1: "Health Mitigation Capacity [0-5]",
          score1: r.indexes.health.healthMitigationCapacityIndex,
          subtitle2: "Occupational Health and Safety [0-5]",
          score2: Math.round(r.indexes.health.occupationalHealthAndSafetyScore * 5), // Scale 0-1 to 1-5 stars if needed
        },
        institutionalCardData: { // institutional
          category: r.businessModel,
          title: "Institutional",
          subtitle1: "Financial and Incentive Mechanism [0-5]",
          score1: r.indexes.institutions.financialAndIncentiveMechanismScore,
          subtitle2: "Land Access and Tenure Security [0-5]",
          score2: r.indexes.institutions.landAccessAndTenureSecurityScore,
          subtitle3: "Policy and Institutional Support [0-5]",
          score3: r.indexes.institutions.policyAndInstitutionalSupportScore,
          subtitle4: "Regulatory Compliance Ease [0-5]",
          score4: r.indexes.institutions.regulatoryComplianceEase,
        },
        Environment1CardData: { // environment 1
          category: r.businessModel,
          title: "Environment 1",
          Data: [
            {
              label: "Noise Pollution [0-5]",
              value: r.indexes.environment.noisePollutionRisk,

            },
            {
              label: "Odour Pollution [0-5]",
              value: r.indexes.environment.odourPollutionRisk,

            },
            {
              label: "Biodiversity Impact Score [0-5]",
              value: r.indexes.environment.biodiversityImpactScore,

            },
            {
              label: "Waste Diversion [0-5]",
              value: r.indexes.environment.wasteDiversionRate,

            }
          ],
        },
        environment2CardData: { // environment 2
          category: r.businessModel,
          title: "Environment 2",
          Data: [
            { label: "Water Use [0-5]", value: r.indexes.environment.waterUse },
            { label: "Land Use [0-5]", value: r.indexes.environment.landUse },
            { label: "Energy Use [0-5]", value: r.indexes.environment.energyUse },
            { label: "GHG Emissions [tCO2e/t]", value: r.indexes.environment.netGHG },
          ],
        },
        marketCardData: { // market
          category: r.businessModel,
          title: "Market",
          Data: [
            {
              label: "MVS [0-5]",
              value: r.indexes.markets.marketAccessibilityAndInclusivenessScore,
            },
            {
              label: "CSI [0-5]",
              value: r.indexes.markets.marketDemandPotentialScore,
            },
            {
              label: "MDC [0-5]",
              value: r.indexes.markets.marketInfrastructureAndDistributionScore,
            },
            {
              label: "GA [0-5]",
              value: r.indexes.markets.priceCompetitivenessScore,
            },
            {
              label: "PMP [0-5]",
              value: r.indexes.markets.standardCertificationAndBuyerConfidenceScore,
            },
          ]
        },

        technologyCardData: { // technology 1
          category: r.businessModel,
          title: "Technology 1",
          Data: [
            {
              label: "Compatibility [0-5]",
              value: r.indexes.technology.compatibilityIndex,
            },
            {
              label: "Maturity & readiness [0-5]",
              value: r.indexes.technology.maturityAndReadinessLevel,
            },
            {
              label: "Complexity [0-5]",
              value: r.indexes.technology.complexityIndex,
            },
          ]
        },
        peopleCardData: { // people
          category: r.businessModel,
          title: "People",
          subtitle1: "Employment Rate [0-5]",
          score1: r.indexes.people.jobCreationPotential,
          subtitle2: "Gender & Youth Inclusion Index [0-5]",
          score2: r.indexes.people.genderAndYouthInclusionIndex,
          subtitle3: "Social Acceptance Score [0-5]",
          score3: r.indexes.people.socialAcceptanceScore,
        },
      };
    });
    setCardDataMap(newCardDataMap);
  };
  const [selectedDomain, setSelectedDomain] = useState<string>("");
  const [activeChartIndex, setActiveChartIndex] = useState<number>(0);
  const [showDetails, setShowDetails] = useState<string | null>(null);
  const [cardTypeOrder, setCardTypeOrder] = useState<string[]>([
    "number",
    "feasibility",
    "cardlist",
    "dot",
    "HealthCard",
    "institutionCard",
    "environment1",
    "environment2",
    "technology2",
  ]);
  const [isDownloadOpen, setIsDownloadOpen] = useState<boolean>(false);
  const [isSaveAnalysisOpen, setIsSaveAnalysisOpen] = useState<boolean>(false);

  const handleOpenDownload = () => {
    setIsDownloadOpen(true);
  };

  const handleOpenSaveAnalysis = () => {
    if (isLoggedIn) {
      setIsSaveAnalysisOpen(true);
    } else {
      setIsLoginOpen(true);
    }
  };

  const handleLoginSuccess = () => {
    setIsSaveAnalysisOpen(true);
  };

  const handleSaveAnalysis = async (analysisName: string) => {
    if (!currentPayload) {
      console.error("No parameters to save");
      return;
    }

    try {
      const savePayload = {
        name: analysisName,
        parameters: currentPayload
      };

      console.log("Saving analysis:", savePayload);
      await saveAnalysis(savePayload);
      console.log("Analysis saved successfully");
      setIsSaveAnalysisOpen(false);
      navigate("/welcome");

    } catch (error) {
      console.error("Error saving analysis:", error);
      // Ideally show an error message to the user here
    }
  };

  const rotateCards = (direction: "left" | "right") => {
    const currentOrder = [...cardTypeOrder];

    if (direction === "left") {
      const first = currentOrder.shift();
      if (first) currentOrder.push(first);
    } else {
      const last = currentOrder.pop();
      if (last) currentOrder.unshift(last);
    }

    setCardTypeOrder(currentOrder);
  };

  // Initialize state from location payload if available, else use context default
  const [wasteCollectionEfficiency, setWasteCollectionEfficiency] = useState<number>(
    location.state?.payload?.wasteCollectionEfficiency ?? state.plantSettings.wasteCollectionEfficiency
  );
  const [plantCapacityFactor, setPlantCapacityFactor] = useState<number>(
    location.state?.payload?.plantCapacityFactor ?? state.plantSettings.plantCapacityFactor
  );
  const [scaleOfPlant, setScaleOfPlant] = useState<number>(() => {
    if (location.state?.payload?.scaleOfPlantValue) {
      // Reverse map value to index
      const val = location.state.payload.scaleOfPlantValue;
      if (val === 10 || val === 500) return 0; // Small (1000 matches PlantLocation, 10 matches potential old data)
      if (val === 2000) return 1; // Medium
      if (val === 10000) return 2; // Large
      // Fallback: check if 1000 was previously Medium? For now assume PlantLocation is source of Truth.
    }
    return state.plantSettings.scaleOfPlant;
  });
  const [operationalLife, setOperationalLife] = useState<number>(() => {
    if (location.state?.payload?.expectedLifeOfPlant) {
      // Find index of operational life value
      const index = operationalLifeOptions.findIndex(opt => parseInt(opt) === location.state.payload.expectedLifeOfPlant);
      return index >= 0 ? index : state.plantSettings.operationalLife;
    }
    return state.plantSettings.operationalLife;
  });
  const [truckCapacity, setTruckCapacity] = useState<number>(
    location.state?.payload?.truckCapacity ?? state.plantSettings.truckCapacity
  );
  const [averageDistance, setAverageDistance] = useState<number>(
    location.state?.payload?.avgDistanceTraveled ?? state.plantSettings.averageDistance
  );
  const [selectedFuel, setSelectedFuel] = useState<string>(
    location.state?.payload?.fuelType ?? state.plantSettings.selectedFuel
  );

  // Update sliders when context state changes AND no payload is passed
  // If we are viewing a saved analysis, we might NOT want to sync with context if context changes
  // But usually context won't change while we are on this page unless we navigate away and back
  useEffect(() => {
    if (!location.state?.payload) {
      setWasteCollectionEfficiency(state.plantSettings.wasteCollectionEfficiency);
      setPlantCapacityFactor(state.plantSettings.plantCapacityFactor);
      setScaleOfPlant(state.plantSettings.scaleOfPlant);
      setOperationalLife(state.plantSettings.operationalLife);
      setTruckCapacity(state.plantSettings.truckCapacity);
      setAverageDistance(state.plantSettings.averageDistance);
      setSelectedFuel(state.plantSettings.selectedFuel);
    }
  }, [state.plantSettings, location.state]);

  // Memoize the current payload to be used in both effect and save
  const currentPayload = React.useMemo<AnalysisInput>(() => {
    const payload = location.state?.payload;
    if (!payload) return null;

    return {
      ...payload,
      wasteCollectionEfficiency: wasteCollectionEfficiency,
      plantCapacityFactor: plantCapacityFactor,
      // Use mapped value based on index (matching plantLocation.tsx values)
      scaleOfPlantValue: [500, 2000, 10000][scaleOfPlant] || payload.scaleOfPlantValue,
      expectedLifeOfPlant: parseInt(operationalLifeOptions[operationalLife]) || payload.expectedLifeOfPlant,
      truckCapacity: truckCapacity,
      avgDistanceTraveled: averageDistance,
      fuelType: selectedFuel,
      region: payload.region || state.selectedRegion,
    };
  }, [
    location.state,
    wasteCollectionEfficiency,
    plantCapacityFactor,
    scaleOfPlant,
    operationalLife,
    truckCapacity,
    averageDistance,
    selectedFuel
  ]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentPayload) {
        console.log("Sending updated payload to backend:", currentPayload);
        getMCDAResults(currentPayload)
          .then((response) => {
            console.log("Received MCDA results:", response.data);
            processResponse(response.data);
            setAnalysisResponse(response.data);
          })
          .catch((error) => {
            console.error("Error fetching MCDA results:", error);
          });
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [currentPayload]);


  const allCharts = [
    {
      title: "Technology",
      component: (
        <ComparisonBarChart
          title="Technology"
          barData={technologyBarDataState}
          barDatasets={technologyBarDatasetsState}
          tableData={technologyTableDataState}
          colors={technologyColorsState}
          legendLabels={technologyLegendLabelsState}
        />
      ),
    },
    {
      title: "Environment",
      component: (
        <ComparisonRadarChart
          title="Environment"
          radarData={environmentRadarDataState}
          radarDatasets={environmentRadarDatasetsState}
          tableData={environmentTableDataState}
          colors={environmentColorsState}
          legendLabels={environmentLegendLabelsState}
        />
      ),
    },
    {
      title: "Finance",
      component: (
        <ComparisonTable
          title="Finance"
          tableData={financeTableDataState}
          colors={financeColorsState}
          legendLabels={financeLegendLabelsState}
        />
      ),
    },    {
      title: "Institutions",
      component: (
        <ComparisonRadarChart
          title="Institutions"
          radarData={institutionsRadarDataState}
          radarDatasets={institutionsRadarDatasetsState}
          tableData={institutionsTableDataState}
          colors={institutionsColorsState}
          legendLabels={institutionsLegendLabelsState}
        />
      ),
    },
    {
      title: "People",
      component: (
        <ComparisonRadarChart
          title="People"
          radarData={peopleRadarDataState}
          radarDatasets={peopleRadarDatasetsState}
          tableData={peopleTableDataState}
          colors={peopleColorsState}
          legendLabels={peopleLegendLabelsState}
        />
      ),
    },
    {
      title: "Market",
      component: (
        <ComparisonRadarChart
          title="Market"
          radarData={marketsRadarDataState}
          radarDatasets={marketsRadarDatasetsState}
          tableData={marketsTableDataState}
          colors={marketsColorsState}
          legendLabels={marketsLegendLabelsState}
        />
      ),
    },
    {
      title: "Health",
      component: (
        <ComparisonBarChart
          title="Health"
          barData={healthBarDataState}
          barDatasets={healthBarDatasetsState}
          tableData={healthTableDataState}
          colors={healthColorsState}
          legendLabels={healthLegendLabelsState}
        />
      ),
    },
  ];


  const getStoredValue = (key: string): string => {
    try {
      return localStorage.getItem(key) || "";
    } catch {
      return "";
    }
  };

  const getStoredArray = (key: string): string[] => {
    try {
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  };


  // Get selected objective and filter charts
  const selectedObjective = getStoredValue("selectedObjective");
  const enabledCharts = allCharts.map(chart => chart.title);
  const filteredCharts = allCharts.filter(chart => enabledCharts.includes(chart.title));

  const tags = useWizardTags();

  const handleBack = () => {
    navigate("/plant-location");
  };

  const handleDomainClick = (domain: string, enabled: boolean) => {
    if (enabled) {
      if (selectedDomain === domain) {
        setSelectedDomain("");
      } else {
        setSelectedDomain(domain);
        if (domain === "See All") {
          setActiveChartIndex(0);
        }
      }
    }
  };

  const handleTrackClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      const rect = e.currentTarget.getBoundingClientRect();
      const clickY = e.clientY - rect.top;
      const trackHeight = 500 - 24;
      const normalizedPosition = Math.max(
        0,
        Math.min(trackHeight, clickY - 12)
      );

      const exactIndex =
        (normalizedPosition / trackHeight) * (filteredCharts.length - 1);
      const targetIndex = Math.round(exactIndex);

      if (targetIndex >= 0 && targetIndex < filteredCharts.length) {
        setActiveChartIndex(targetIndex);
      }
    }
  };

  const handleScrollDrag = (e: React.MouseEvent<HTMLDivElement>) => {
    const startY = e.clientY;
    const startIndex = activeChartIndex;
    const trackHeight = 500 - 24;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaY = moveEvent.clientY - startY;
      const newPosition =
        (startIndex / (filteredCharts.length - 1)) * trackHeight + deltaY;
      const normalizedPosition = Math.max(
        0,
        Math.min(trackHeight, newPosition)
      );

      const exactIndex =
        (normalizedPosition / trackHeight) * (filteredCharts.length - 1);
      const lowerIndex = Math.floor(exactIndex);
      const upperIndex = Math.ceil(exactIndex);
      const fraction = exactIndex - lowerIndex;

      let targetIndex = lowerIndex;
      if (fraction > 0.5 && upperIndex < filteredCharts.length) {
        targetIndex = upperIndex;
      }

      if (
        targetIndex !== activeChartIndex &&
        targetIndex >= 0 &&
        targetIndex < filteredCharts.length
      ) {
        setActiveChartIndex(targetIndex);
      }
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const renderChart = () => {
    if (selectedDomain === "See All") {
      return (
        <div className="mcdaResult-domain-chart-wrapper">
          <button
            className="mcdaResult-close-button"
            onClick={() => setSelectedDomain("")}
            aria-label="Close comparison"
          >
            <X size={20} />
          </button>
          <div className="mcdaResult-see-all-container">
            <div className="mcdaResult-chart-display">
              {filteredCharts[activeChartIndex].component}
            </div>
            <div className="mcdaResult-scroll-navigation">
              <div className="mcdaResult-scroll-track" onClick={handleTrackClick}>
                <div
                  className="mcdaResult-scroll-fill"
                  style={{
                    height: `${(activeChartIndex / (filteredCharts.length - 1)) * (500 - 24) +
                      12
                      }px`,
                  }}
                />
                <div
                  className="mcdaResult-scroll-thumb"
                  style={{
                    top: `${(activeChartIndex / (filteredCharts.length - 1)) * (500 - 24)
                      }px`,
                  }}
                  onMouseDown={handleScrollDrag}
                >
                  <ChevronUp size={12} />
                  <ChevronDown size={12} />
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (selectedDomain === "Environment") {
      return (
        <div className="mcdaResult-domain-chart-wrapper">
          <button
            className="mcdaResult-close-button"
            onClick={() => setSelectedDomain("")}
            aria-label="Close comparison"
          >
            <X size={20} />
          </button>
          <ComparisonRadarChart
            title="Environment"
            radarData={environmentRadarDataState}
            radarDatasets={environmentRadarDatasetsState}
            tableData={environmentTableDataState}
            colors={environmentColorsState}
            legendLabels={environmentLegendLabelsState}
          />
        </div>
      );
    }

    if (selectedDomain === "Finance") {

      console.log("xxxx " + JSON.stringify(cumulativeCashFlowDatasetsState, null, 2));
      
      return (
        <div className="mcdaResult-domain-chart-wrapper">
          <button
            className="mcdaResult-close-button"
            onClick={() => setSelectedDomain("")}
            aria-label="Close comparison"
          >
            <X size={20} />
          </button>
          <TabPanel
            name="finance-tabs"
            title="Finance"

            tabs={[
              {
                label: "Finance Comparison",
                content: (
                  <ComparisonTable
                    tableData={financeTableDataState}
                    colors={financeColorsState}
                    legendLabels={financeLegendLabelsState}
                  />
                ),
              },
              {
                label: "Cashflow",
                content: (
                  <ComparisonLineChart
                    data={cumulativeCashFlowState}
                    datasets={cumulativeCashFlowDatasetsState}
                  />
                ),
              },
            ]}
          />
        </div>
      );
}

    if (selectedDomain === "Technology") {
      return (
        <div className="mcdaResult-domain-chart-wrapper">
          <button
            className="mcdaResult-close-button"
            onClick={() => setSelectedDomain("")}
            aria-label="Close comparison"
          >
            <X size={20} />
          </button>
          <ComparisonBarChart
            title="Technology"
            barData={technologyBarDataState}
            barDatasets={technologyBarDatasetsState}
            tableData={technologyTableDataState}
            colors={technologyColorsState}
            legendLabels={technologyLegendLabelsState}
          />
        </div>
      );
    }

    if (selectedDomain === "Institutions") {
      return (
        <div className="mcdaResult-domain-chart-wrapper">
          <button
            className="mcdaResult-close-button"
            onClick={() => setSelectedDomain("")}
            aria-label="Close comparison"
          >
            <X size={20} />
          </button>
          <ComparisonRadarChart
            title="Institutions"
            radarData={institutionsRadarDataState}
            radarDatasets={institutionsRadarDatasetsState}
            tableData={institutionsTableDataState}
            colors={institutionsColorsState}
            legendLabels={institutionsLegendLabelsState}
          />
        </div>
      );
    }

    if (selectedDomain === "People") {
      return (
        <div className="mcdaResult-domain-chart-wrapper">
          <button
            className="mcdaResult-close-button"
            onClick={() => setSelectedDomain("")}
            aria-label="Close comparison"
          >
            <X size={20} />
          </button>
          <ComparisonRadarChart
            title="People"
            radarData={peopleRadarDataState}
            radarDatasets={peopleRadarDatasetsState}
            tableData={peopleTableDataState}
            colors={peopleColorsState}
            legendLabels={peopleLegendLabelsState}
          />
        </div>
      );
    }

    if (selectedDomain === "Market") {
      return (
        <div className="mcdaResult-domain-chart-wrapper">
          <button
            className="mcdaResult-close-button"
            onClick={() => setSelectedDomain("")}
            aria-label="Close comparison"
          >
            <X size={20} />
          </button>
          <ComparisonRadarChart
            title="Markets"
            radarData={marketsRadarDataState}
            radarDatasets={marketsRadarDatasetsState}
            tableData={marketsTableDataState}
            colors={marketsColorsState}
            legendLabels={marketsLegendLabelsState}
          />
        </div>
      );
    }

    if (selectedDomain === "Health") {
      return (
        <div className="mcdaResult-domain-chart-wrapper">
          <button
            className="mcdaResult-close-button"
            onClick={() => setSelectedDomain("")}
            aria-label="Close comparison"
          >
            <X size={20} />
          </button>
          <ComparisonBarChart
            title="Health"
            barData={healthBarDataState}
            barDatasets={healthBarDatasetsState}
            tableData={healthTableDataState}
            colors={healthColorsState}
            legendLabels={healthLegendLabelsState}
          />
        </div>
      );
    }

    if (showDetails) {
      const cardData = cardDataMap[showDetails];
      if (!cardData) return null;

      const renderCard = (cardType: string, isHighlighted: boolean) => {
        switch (cardType) {
          case "Environment":
            return (
              <NumberCard
                category={cardData.title}
                title="Environment"
                minValue={0}
                maxValue={maxOfStructured(cardData.numberCardData)}
                items={cardData.numberCardData}
                highlight={isHighlighted}
              />
            );
          case "feasibility":
            return (
              <Feasibility
                position={cardData.position}
                total={Object.keys(cardDataMap).length}
                title={cardData.title}
                value={cardData.value}
                min={0}
                max={100}
                minColor="var(--color-orange)"
                maxColor="var(--color-green)"
                color={cardData.color}
                highlight={isHighlighted}
                disableDetails={true}
                onDetailsClick={() => { }}
              />
            );
          case "finance":
            return (
              <CardList
                category={cardData.title}
                title="Finance"
                items={cardData.cardListData}
                highlight={isHighlighted}
              />
            );
          case "technology1":
            return (
              <DotCard
                category={cardData.dotCardData.category}
                title={cardData.dotCardData.title}
                subtitle1={cardData.dotCardData.subtitle1}
                value={cardData.dotCardData.value}
                unit={cardData.dotCardData.unit}
                subtitle2={cardData.dotCardData.subtitle2}
                score={cardData.dotCardData.score}
                highlight={isHighlighted}
              />
            );
          case "HealthCard":
            return (
              <HealthCard
                category={cardData.title}
                title="Health"
                subtitle1={cardData.healthCardData.subtitle1}
                subtitle2={cardData.healthCardData.subtitle2}
                score1={cardData.healthCardData.score1}
                score2={cardData.healthCardData.score2}
                highlight={isHighlighted}


              />
            );
          case "institutionCard":
            return (
              <InstitutionCard
                category={cardData.title}
                title="Institutional"
                subtitle1={cardData.institutionalCardData.subtitle1}
                subtitle2={cardData.institutionalCardData.subtitle2}
                subtitle3={cardData.institutionalCardData.subtitle3}
                score3={cardData.institutionalCardData.score3}
                subtitle4={cardData.institutionalCardData.subtitle4}
                score4={cardData.institutionalCardData.score4}
                score1={cardData.institutionalCardData.score1}
                score2={cardData.institutionalCardData.score2}
                highlight={isHighlighted} />
            );

          case "environment1":

            return (
              <LineDotCard
                category={cardData.title}
                title="Environment 1"
                Data={cardData.Environment1CardData.Data}
                score={0} // Unused in component logic?
                highlight={isHighlighted}
              />
            );

          case "environment2":

            return (
              <LineDotCard
                category={cardData.title}
                title="Environment 2"
                Data={cardData.environment2CardData.Data}
                score={0} // Unused in component logic?
                highlight={isHighlighted}
              />
            );

          case "technology2":

            return (
              <LineDotCard
                category={cardData.title}
                title="Technology "
                Data={cardData.technologyCardData.Data}
                score={0} // Unused in component logic?
                highlight={isHighlighted}
              />
            );


          case 'marketCard':

            return (
              <NumberCard
                category={cardData.title}
                title="Market"
                minValue={0}
                maxValue={5}
                items={cardData.marketCardData.Data}
                highlight={isHighlighted}
              />
            );

          case 'peopleCard':

            return (
              <PeopleCard
                category={cardData.title}
                title="People"
                subtitle1={cardData.peopleCardData.subtitle1}
                score1={cardData.peopleCardData.score1}
                subtitle2={cardData.peopleCardData.subtitle2}
                score2={cardData.peopleCardData.score2}
                subtitle3={cardData.peopleCardData.subtitle3}
                score3={cardData.peopleCardData.score3}
                highlight={isHighlighted}
              />
            );

          default:
            return null;
        }
      };

      const visibleCards = cardTypeOrder.slice(0, 5);
      console.log("Visible Cards", visibleCards);
      return (
        <div className="mcdaResult-details-container">
          <button
            className="mcdaResult-nav-button mcdaResult-nav-left"
            onClick={() => rotateCards("left")}
          >
            <ChevronLeft size={20} />
          </button>

          <button
            className="mcdaResult-close-button"
            onClick={() => setShowDetails(null)}
          >
            <X size={20} />
          </button>

          <button
            className="mcdaResult-nav-button mcdaResult-nav-right"
            onClick={() => rotateCards("right")}
          >
            <ChevronRight size={20} />
          </button>

          <div className="mcdaResult-details-row">

            {renderCard(visibleCards[0], false)}
            {renderCard(visibleCards[1], true)}
            {renderCard(visibleCards[2], false)}

          </div>
        </div>
      );
    }

    // Default view: Feasibility cards for all business models
    const businessModelKeys = Object.keys(cardDataMap);

    // If no data yet, return null or loader
    if (businessModelKeys.length === 0) return null;
    const objectiveCardOrder = {
      "Profit Maximization": ["feasibility", "technology1", "finance", "Environment"],
      "Yield Maximization": ["feasibility", "technology1", "technology2"],
      "Welfare Optimization": ["feasibility", "peopleCard"],
      "Environmental Maximization": ["feasibility", "environment1", "environment2", "HealthCard"],
      "Enabling Environment": ["feasibility", "institutionCard", "marketCard"]
    };

    return (
      <div className="mcdaResult-feasibility-row">
        {businessModelKeys.map((key) => {
          const data = cardDataMap[key];
          return (
            <Feasibility
              key={key}
              position={data.position}
              total={businessModelKeys.length}
              title={data.title}
              value={data.value}
              min={0}
              max={100}
              minColor="var(--color-orange)"
              maxColor="var(--color-green)"
              color={data.color}
              highlight={false} // Logic for highlighting can be added based on rank if needed
              disableDetails={false}
              onDetailsClick={() => {
                setShowDetails(key);
                const order = objectiveCardOrder[selectedObjective as keyof typeof objectiveCardOrder] || [
                  "Environment",
                  "feasibility",
                  "finance",
                  "technology",
                  "HealthCard",
                  "institutionCard",
                  "environment1",
                  "environment2",
                  "technology2",
                  "marketCard",
                  "peopleCard",
                ];
                setCardTypeOrder(order);
              }}
            />
          );
        })}
      </div>
    );
  };
  // ... (I need to be careful with context matching, probably better to split into multiple replacements if range is too large or discontinuous)


  // Create processed domains with Health added if needed, and enabled status updated
  const localDomains = [...domains];
  if (!localDomains.find(d => d.label === "Health")) {
    const seeAllIndex = localDomains.findIndex(d => d.label === "See All");
    if (seeAllIndex !== -1) {
      localDomains.splice(seeAllIndex, 0, { label: "Health", enabled: true });
    } else {
      localDomains.push({ label: "Health", enabled: true });
    }
  }

  const processedDomains = localDomains.map(domain => ({
    ...domain,
    enabled: domain.label === "See All" ? true : enabledCharts.includes(domain.label)
  }));

  return (
    <div className="mcdaResult-container">
      <VerticalNavigation
        title="MCDA Results"
        description="See Your Top Business Models And How They Perform Across Key Areas"
        tags={tags}
        showProgressBar={false}
        onMenuToggle={setIsMenuOpen}
        onBack={handleBack}
      />

      {!isMenuOpen && (
        <div className="mcdaResult-content">
          <div className="mcdaResult-inner">
            <div className="mcdaResult-columns">
              <div className="mcdaResult-column-left">
                <div className="mcdaResult-adjust-box">
                  <div className="mcdaResult-adjust-header">
                    <div className="mcdaResult-adjust-title-group">
                      <h3 className="mcdaResult-field-title">Adjust Results</h3>
                      <Tooltip
                        title="Adjust Results"
                        content="This toolbox allows you to fine-tune your input choices and observe how these adjustments influence the final outcomes. Click the refresh button to update the results with your latest selections. For more control and customization, click the gear icon to access advanced settings and configuration options."
                        offsetY={-12}
                      />
                    </div>

                  </div>

                  <div className="mcdaResult-adjust-controls">
                    <div className="mcdaResult-control-group">
                      <Slider2
                        title="Waste Collection Efficiency"
                        value={wasteCollectionEfficiency}
                        onChange={setWasteCollectionEfficiency}
                        min={0}
                        max={100}
                        unit="%"
                      />
                    </div>

                    <div className="mcdaResult-control-group">
                      <Slider2
                        title="Plant Capacity Factor"
                        value={plantCapacityFactor}
                        onChange={setPlantCapacityFactor}
                        min={0}
                        max={100}
                        unit="%"
                      />
                    </div>

                    <div className="mcdaResult-control-group">
                      <SliderConnector
                        type="3-3"
                        title3="Scale of Plant"
                        options3={scaleOptions}
                        selectedIndex3={scaleOfPlant}
                        onChange3={setScaleOfPlant}
                        title3Second="Expected Operational Life of Plant"
                        options3Second={operationalLifeOptions}
                        selectedIndex3Second={operationalLife}
                        onChange3Second={setOperationalLife}
                        disableSecondSlider={true}
                      />
                    </div>

                    <div className="mcdaResult-control-group">
                      <TruckCapacitySelector
                        title="Truck Capacity"
                        selectedIndex={truckCapacity}
                        onChange={setTruckCapacity}
                      />
                    </div>

                    <div className="mcdaResult-control-group">
                      <Slider2
                        title="Average Distance"
                        value={averageDistance}
                        onChange={setAverageDistance}
                        min={0}
                        max={40}
                        unit="km"
                      />
                    </div>

                    <div className="mcdaResult-control-group">
                      <RadioGroup
                        title="Fuel Type"
                        options={["Petrol/Gasoline", "Diesel", "Fuel Oil", "LPG", "Natural Gas", "Hydrogen"]}
                        name="fuel"
                        value={selectedFuel}
                        onChange={setSelectedFuel}
                        enabledOption="Diesel"
                        tooltipTitle="Fuel Type"
                        tooltipContent="Only Diesel is available in this version"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="mcdaResult-column-right">
                <div className="mcdaResult-ranking-section">
                  <div className="mcdaResult-section-header">
                    <h3 className="mcdaResult-field-title">
                      Business Models Ranking
                    </h3>
                    <Tooltip
                      title="Business Models Ranking"
                      content="This section presents a ranking of the selected business models based on their performance across the various indicators of the MCDA analysis. The model at the centre is considered the most suitable for the area and specifics defined by the user, as it outperforms others in the majority of key criteria and conditions."
                      offsetY={-12}
                    />
                  </div>

                  <div className="mcdaResult-comparison-chart">
                    {renderChart()}
                  </div>
                </div>

                <div className="mcdaResult-compare-section">
                  <div className="mcdaResult-section-header">
                    <h3 className="mcdaResult-field-title">
                      Compare Across Domains
                    </h3>
                    <Tooltip
                      title="Compare Across Domains"
                      content="This section gives you the possibility of comparing business models under specific domain of interests: Financial, Environmental, Market, Institutional, Technical, and People (Social + Health). The comparison depends on the goals you set at the beginning, whether you're aiming for cost savings, profit, welfare, or sustainability. That way, the models are ranked and displayed according to what matters most to you, helping you make a more informed, goal-driven decision for your circular business strategy."
                      offsetY={-12}
                    />
                  </div>

                  <div className="mcdaResult-domain-buttons">
                    {processedDomains.map((domain) => (
                      <button
                        key={domain.label}
                        className={`mcdaResult-domain-btn ${selectedDomain === domain.label
                          ? "mcdaResult-domain-btn-active"
                          : ""
                          } ${!domain.enabled
                            ? "mcdaResult-domain-btn-disabled"
                            : ""
                          }`}
                        onClick={() =>
                          handleDomainClick(domain.label, domain.enabled)
                        }
                        disabled={!domain.enabled}
                      >
                        {domain.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="mcdaResult-button-container">
              <button
                className="mcdaResult-download-button mcdaResult-download-button--active"
                onClick={handleOpenDownload}
                disabled={!analysisResponse}
              >
                Download Report
              </button>
              <button
                className="mcdaResult-save-button mcdaResult-save-button--active"
                onClick={handleOpenSaveAnalysis}
              >
                Save Analysis →
              </button>
            </div>
          </div>
        </div>
      )}

      {analysisResponse && (
        <Download
          isOpen={isDownloadOpen}
          onClose={() => setIsDownloadOpen(false)}
          analysisInput={analysisResponse.originalInput}
          analysisResults={analysisResponse.analysisResults}
        />
      )}

      <SaveAnalysisModal
        isOpen={isSaveAnalysisOpen}
        onClose={() => setIsSaveAnalysisOpen(false)}
        onSave={handleSaveAnalysis}
      />

      <Login isOpen={isLoginOpen} onClose={() => setIsLoginOpen(false)} onLogin={handleLoginSuccess} />


    </div>

  );
};

export default MCDAResults;

